/**
 * NitHub — the hub's own app store (https://apps.nithishb.com/get/), which
 * installs and updates our apps (NitHub app contract §5).
 *
 * It updates MyDeck silently, but only while it is the app that installed
 * MyDeck last (Android's "installer of record"). MyDeck's own updater would
 * take that role back, so when NitHub is on the phone, "Update" hands over to
 * it; the built-in updater is the fallback for phones without it.
 * `NitHubPlugin.java` does the asking. Everything degrades to "no NitHub".
 * The plugin is never awaited itself (a Capacitor plugin is a Proxy that
 * answers `then`).
 */

import { isNative } from "./index";

export const NITHUB_PAGE = "https://apps.nithishb.com/get/";

export interface NitHubStatus {
  /** NitHub is on this phone and can open MyDeck's page. */
  installed: boolean;
  /** It also installed this copy, so its updates need no tap (Android 12+). */
  owner: boolean;
}

export const NO_NITHUB: NitHubStatus = { installed: false, owner: false };

type Plugin = { status(): Promise<{ installed?: boolean; owner?: boolean }>; open(): Promise<void> };
let plugin: Plugin | null = null;
let registering: Promise<void> | null = null;

async function register(): Promise<void> {
  registering ??= import("@capacitor/core").then(
    ({ registerPlugin }) => {
      plugin = registerPlugin<Plugin>("NitHub");
    },
    () => undefined,
  );
  await registering;
}

export async function nitHubStatus(): Promise<NitHubStatus> {
  if (!isNative()) return NO_NITHUB;
  await register();
  try {
    const res = await plugin?.status();
    return { installed: res?.installed === true, owner: res?.installed === true && res?.owner === true };
  } catch {
    return NO_NITHUB;
  }
}

/** Open MyDeck's page in NitHub. False when it couldn't (gone, or no plugin). */
export async function openInNitHub(): Promise<boolean> {
  if (!isNative()) return false;
  await register();
  try {
    await plugin?.open();
    return true;
  } catch {
    return false;
  }
}

/** The hub's NitHub page, in the phone's browser. */
export function getNitHub(): void {
  window.open(NITHUB_PAGE, "_system");
}
